import { useCallback, useEffect, useRef } from 'react'
import { Box } from '@achieve/ascend'
import { max, scaleLinear, scaleBand, select } from 'd3'
import { DataSectionTest, numberFormatCurrency } from 'constants/DebtAssessment'
import { useGraphData } from './useGraphData'
import { useContainerWidth } from './useContainerWidth'
import { MARGIN, addGridLines, setupAxes, addStripedPatterns } from './d3Helpers'
import { GraphTooltip, TOOLTIP_PSEUDO_STYLES } from './GraphTooltip'

function renderTooltipContent({ title, titleIcon, achieveLogo, value, titleStyle }) {
  const iconHtml = titleIcon
    ? `<img alt="Achieve logo" width="20" height="16" src="${achieveLogo}" />`
    : ''
  return `
    <div class="text-[13px] rounded-t-[5px] p-[5px] flex gap-[5px] items-center justify-center ${titleStyle}">
      ${iconHtml}${title}
    </div>
    <div class="value">${value}</div>`
}

function positionTooltip({
  tooltipRef,
  lineClassname,
  xAdd,
  yAdd,
  graphWidth,
  showTooltip,
  ...contentProps
}) {
  const mobileSpacing = graphWidth < 713 ? 30 : 0
  const pathElement = document.getElementsByClassName(lineClassname)[0]
  if (!pathElement || !tooltipRef.current || !showTooltip) return

  const x = Math.round(pathElement.getAttribute('x')) + Math.round(xAdd + mobileSpacing)
  const y = Math.round(pathElement.getAttribute('y')) + Math.round(yAdd)

  select(tooltipRef.current).transition().duration(200).style('opacity', 1)

  select(tooltipRef.current)
    .html(renderTooltipContent(contentProps))
    .style('left', x + 'px')
    .style('top', y + 'px')
}

export const DatGraphBars = ({
  graphHeight = 350,
  data = DataSectionTest,
  textLegendAxisY = 'Monthly',
  subtextLegendAxisY = 'Payment',
  achieveLogo = 'https://https://images.ctfassets.net/80jeq2aeq4l1/3GI83dKykjtYKjZj9Givxj/7a302f8d5ad4f8625cd099df8167d013/a_logo.svg?w=3840',
  tooltipTitleAchieve = 'achieve',
  tooltipTitleCurrent = 'Current',
  tooltipTitleWait = 'If you wait',
  productType = 'AR',
  productTypeTerms = 0,
  colorGraph = '',
  showAchieveTooltip = true,
  showCurrentTooltip = true,
  showWaitTooltip = true,
  selectedValue,
}) => {
  const tooltipRefAchieve = useRef(null)
  const tooltipRefCurrent = useRef(null)
  const tooltipRefWait = useRef(null)
  const svgChartRef = useRef(null)

  const { containerRef, chartRef, width: graphWidth } = useContainerWidth()
  const getGraphData = useGraphData({ data, productType, productTypeTerms })

  const createGraph = useCallback(() => {
    const { achieveOffer, currentOffer, waitOffer } = getGraphData()

    const margin = MARGIN
    const width = graphWidth - margin.left - margin.right
    const height = graphHeight - margin.top - margin.bottom

    if (svgChartRef.current) svgChartRef.current.remove()

    const svg = select(chartRef.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top - 4})`)

    svgChartRef.current = svg

    if (colorGraph) {
      svg.append('rect').attr('width', width).attr('height', height).attr('fill', colorGraph)
    }

    setupAxes(svg, { width, height, margin, selectedValue, subtextLegendAxisY, textLegendAxisY })
    addGridLines(width, 300, svg)

    const barData = [
      { category: 'Achieve', value: achieveOffer.monthlyPayment, pattern: 'achieveStripes' },
      { category: 'Current', value: currentOffer.monthlyPayment, pattern: 'currentStripes' },
      { category: 'If you wait', value: waitOffer.monthlyPayment, pattern: 'waitStripes' },
    ]

    const xScale = scaleBand()
      .domain(barData.map((d) => d.category))
      .range([
        graphWidth < 600 ? margin.left / 2 : margin.left,
        graphWidth < 600 ? width + margin.left / 2 : width - margin.right,
      ])
      .padding(0.1)

    const yScale = scaleLinear()
      .domain([0, max(barData, (d) => d.value)])
      .nice()
      .range([height - margin.bottom, margin.top])

    const highestPoints = barData.map((d) => ({
      x: xScale(d.category) + xScale.bandwidth() / 2,
      y: yScale(d.value),
    }))

    addStripedPatterns(svg, highestPoints)

    svg
      .selectAll('.bar')
      .data(barData)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', (d) => xScale(d.category))
      .attr('y', (d) => yScale(d.value) + 50)
      .attr('fill', (d) => `url(#${d.pattern})`)
      .attr('width', graphWidth < 713 ? 32 : 64)
      .attr('height', (d) => height - margin.bottom - yScale(d.value) - 50)

    const tooltipConfigs = [
      {
        show: showAchieveTooltip,
        title: tooltipTitleAchieve,
        titleIcon: true,
        value: `${numberFormatCurrency(achieveOffer.monthlyPayment)}/mo`,
        tooltipRef: tooltipRefAchieve,
        lineClassname: 'barLine1',
        titleStyle: 'border-0 bg-[#3300ff] text-white',
      },
      {
        show: showCurrentTooltip,
        title: tooltipTitleCurrent,
        value: `${numberFormatCurrency(currentOffer.monthlyPayment)}/mo`,
        tooltipRef: tooltipRefCurrent,
        lineClassname: 'barLine2',
        titleStyle: 'bg-[#e7ecf3] text-[#5c708a]',
      },
      {
        show: showWaitTooltip,
        title: tooltipTitleWait,
        value: `${numberFormatCurrency(waitOffer.monthlyPayment)}/mo`,
        tooltipRef: tooltipRefWait,
        lineClassname: 'barLine3',
        titleStyle: 'bg-[#fff5f8] text-[#cf264e]',
        xAdd: graphWidth < 460 ? -20 : undefined,
      },
    ]

    tooltipConfigs.forEach(({ show, xAdd: customXAdd, ...cfg }) => {
      if (!show) return
      positionTooltip({
        ...cfg,
        achieveLogo,
        xAdd: customXAdd ?? -40,
        yAdd: -5,
        graphWidth,
        showTooltip: show,
      })
    })
  }, [
    graphWidth,
    graphHeight,
    colorGraph,
    selectedValue,
    subtextLegendAxisY,
    textLegendAxisY,
    showAchieveTooltip,
    showCurrentTooltip,
    showWaitTooltip,
    tooltipTitleAchieve,
    tooltipTitleCurrent,
    tooltipTitleWait,
    achieveLogo,
    getGraphData,
    chartRef,
  ])

  useEffect(() => {
    if (graphWidth > 0 && data) createGraph()
  }, [graphWidth, graphHeight, data, createGraph])

  return (
    <Box className="min-w-[328px] max-w-[1024px] relative" ref={containerRef}>
      <svg className="w-full max-w-full" height={graphHeight} id="barchart" ref={chartRef} />
      <GraphTooltip ref={tooltipRefAchieve} show={showAchieveTooltip} testId="Achieve" />
      <GraphTooltip ref={tooltipRefCurrent} show={showCurrentTooltip} testId="Current" />
      <GraphTooltip ref={tooltipRefWait} show={showWaitTooltip} testId="Wait" />
      <style dangerouslySetInnerHTML={{ __html: TOOLTIP_PSEUDO_STYLES }} />
    </Box>
  )
}
